import { classifySource, visitPageHost, visitPagePath } from './source';
import { formatDelta, monthDay, startOfDay, weekdayLabel } from './format';
import { cityToProvince, detectSpider, inferIsp, parseSearch, referrerHost, socialName } from './parse';
import type { DateBounds } from './range';
import type { DayPoint, NamedCount, SourceChannel, Visit } from './types';

const CHANNELS: SourceChannel[] = ['直接访问', '搜索引擎', '社交媒体', '外部链接'];
const SESSION_GAP = 30 * 60 * 1000;

function inRange(visits: Visit[], from: Date, to: Date): Visit[] {
  const a = from.getTime();
  const b = to.getTime();
  return visits.filter((v) => {
    const t = new Date(v.createdAt).getTime();
    return t >= a && t < b;
  });
}

function unique(values: string[]): number {
  return new Set(values.filter(Boolean)).size;
}

function countBy(list: string[]): NamedCount[] {
  const map = new Map<string, number>();
  list.forEach((name) => {
    const key = name || '未知';
    map.set(key, (map.get(key) || 0) + 1);
  });
  return [...map.entries()]
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

type Session = {
  visitorId: string;
  pages: Visit[];
};

export function buildSessions(visits: Visit[]): Session[] {
  const grouped = new Map<string, Visit[]>();
  [...visits]
    .sort((a, b) => +new Date(a.createdAt) - +new Date(b.createdAt))
    .forEach((v) => {
      const list = grouped.get(v.visitorId) || [];
      list.push(v);
      grouped.set(v.visitorId, list);
    });

  const sessions: Session[] = [];
  grouped.forEach((list, visitorId) => {
    let current: Visit[] = [];
    list.forEach((visit) => {
      if (!current.length) {
        current = [visit];
        return;
      }
      const prev = new Date(current[current.length - 1].createdAt).getTime();
      const next = new Date(visit.createdAt).getTime();
      if (next - prev > SESSION_GAP) {
        sessions.push({ visitorId, pages: current });
        current = [visit];
      } else {
        current.push(visit);
      }
    });
    if (current.length) sessions.push({ visitorId, pages: current });
  });
  return sessions;
}

export function firstSeenMap(visits: Visit[]): Map<string, number> {
  const map = new Map<string, number>();
  visits.forEach((v) => {
    const t = new Date(v.createdAt).getTime();
    const prev = map.get(v.visitorId);
    if (prev === undefined || t < prev) map.set(v.visitorId, t);
  });
  return map;
}

export function summarizePeriod(visits: Visit[], allVisits?: Visit[], periodFrom?: number) {
  const pv = visits.length;
  const uv = unique(visits.map((v) => v.visitorId));
  const ip = unique(visits.map((v) => v.ip));
  const sessions = buildSessions(visits);
  const bounce = sessions.filter((s) => s.pages.length === 1).length;
  const bounceRate = sessions.length ? bounce / sessions.length : 0;
  const withDuration = visits.filter((v) => v.durationMs > 0);
  const avgDuration =
    withDuration.length === 0
      ? 0
      : withDuration.reduce((sum, v) => sum + v.durationMs, 0) / withDuration.length;
  const pagesPerUv = uv ? pv / uv : 0;

  const seen = firstSeenMap(allVisits || visits);
  const from =
    periodFrom ??
    (visits.length ? Math.min(...visits.map((v) => new Date(v.createdAt).getTime())) : 0);
  const visitorIds = [...new Set(visits.map((v) => v.visitorId))];
  const newUv = visitorIds.filter((id) => (seen.get(id) || Date.now()) >= from).length;
  const returnUv = Math.max(0, uv - newUv);

  return {
    pv,
    uv,
    ip,
    sessions: sessions.length,
    bounceRate,
    avgDuration,
    pagesPerUv,
    newUv,
    returnUv,
  };
}

export function buildOverview(visits: Visit[], bounds?: DateBounds) {
  const todayStart = startOfDay();
  const tomorrow = new Date(todayStart);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);
  const weekStart = new Date(todayStart);
  weekStart.setDate(todayStart.getDate() - 6);
  const monthStart = new Date(todayStart);
  monthStart.setDate(todayStart.getDate() - 29);

  const selectedFrom = bounds?.from ?? todayStart;
  const selectedTo = bounds?.to ?? tomorrow;
  const selectedMs = Math.max(1, selectedTo.getTime() - selectedFrom.getTime());
  const prevFrom = new Date(selectedFrom.getTime() - selectedMs);
  const isToday =
    selectedFrom.getTime() === todayStart.getTime() && selectedTo.getTime() === tomorrow.getTime();

  const todayVisits = inRange(visits, todayStart, tomorrow);
  const yesterdayVisits = inRange(visits, yesterdayStart, todayStart);
  const weekVisits = inRange(visits, weekStart, tomorrow);
  const monthVisits = inRange(visits, monthStart, tomorrow);
  const selectedVisits = inRange(visits, selectedFrom, selectedTo);
  const previousVisits = inRange(visits, prevFrom, selectedFrom);

  const today = summarizePeriod(todayVisits, visits, todayStart.getTime());
  const yesterday = summarizePeriod(yesterdayVisits, visits, yesterdayStart.getTime());
  const week = summarizePeriod(weekVisits, visits, weekStart.getTime());
  const month = summarizePeriod(monthVisits, visits, monthStart.getTime());
  const current = summarizePeriod(selectedVisits, visits, selectedFrom.getTime());
  const previous = summarizePeriod(previousVisits, visits, prevFrom.getTime());
  const all = summarizePeriod(visits, visits, 0);

  const hoursElapsed = Math.max(1, (Date.now() - todayStart.getTime()) / 3_600_000);
  const predictedPv = isToday ? Math.round((current.pv / hoursElapsed) * 24) : 0;
  const periodDays = Math.max(1, Math.round(selectedMs / 86_400_000));

  return {
    today,
    yesterday,
    current,
    previous,
    isToday,
    compareLabel: isToday ? '较昨日' : '较上期',
    periodDays,
    week,
    month,
    all,
    predictedPv,
    weekAvg: {
      pv: Math.round(week.pv / 7),
      uv: Math.round(week.uv / 7),
      ip: Math.round(week.ip / 7),
    },
    monthAvg: {
      pv: Math.round(month.pv / 30),
      uv: Math.round(month.uv / 30),
      ip: Math.round(month.ip / 30),
    },
    periodAvg: {
      pv: Math.round(current.pv / periodDays),
      uv: Math.round(current.uv / periodDays),
      ip: Math.round(current.ip / periodDays),
    },
    kpis: [
      {
        key: 'pv',
        label: '浏览量 PV',
        value: current.pv,
        delta: formatDelta(current.pv, previous.pv),
        icon: 'ri-eye-line',
      },
      {
        key: 'uv',
        label: '独立访客 UV',
        value: current.uv,
        delta: formatDelta(current.uv, previous.uv),
        icon: 'ri-user-3-line',
      },
      {
        key: 'ip',
        label: '独立 IP',
        value: current.ip,
        delta: formatDelta(current.ip, previous.ip),
        icon: 'ri-global-line',
      },
      {
        key: 'new',
        label: '新访客',
        value: current.newUv,
        delta: formatDelta(current.newUv, previous.newUv),
        icon: 'ri-user-add-line',
      },
      {
        key: 'bounce',
        label: '跳出率',
        value: current.bounceRate,
        delta: formatDelta(current.bounceRate, previous.bounceRate),
        icon: 'ri-logout-box-line',
        isPercent: true,
      },
      {
        key: 'stay',
        label: '平均停留',
        value: current.avgDuration,
        delta: formatDelta(current.avgDuration, previous.avgDuration),
        icon: 'ri-time-line',
        isDuration: true,
      },
      {
        key: 'depth',
        label: '人均浏览',
        value: current.pagesPerUv,
        delta: formatDelta(current.pagesPerUv, previous.pagesPerUv),
        icon: 'ri-file-list-line',
        isDecimal: true,
      },
      {
        key: 'session',
        label: '访问次数',
        value: current.sessions,
        delta: formatDelta(current.sessions, previous.sessions),
        icon: 'ri-stack-line',
      },
    ],
    totals: all,
  };
}

export function buildTrend(visits: Visit[], days = 7): DayPoint[] {
  const today = startOfDay();
  return Array.from({ length: days }, (_, i) => {
    const date = new Date(today);
    date.setDate(today.getDate() - (days - 1 - i));
    const next = new Date(date);
    next.setDate(date.getDate() + 1);
    const slice = inRange(visits, date, next);
    return {
      label: days > 7 ? monthDay(date) : weekdayLabel(date),
      date: date.toISOString().slice(0, 10),
      pv: slice.length,
      uv: unique(slice.map((v) => v.visitorId)),
    };
  });
}

export function buildHourly(visits: Visit[], day = startOfDay()) {
  const next = new Date(day);
  next.setDate(day.getDate() + 1);
  const slice = inRange(visits, day, next);
  return Array.from({ length: 24 }, (_, hour) => {
    const hourVisits = slice.filter((v) => new Date(v.createdAt).getHours() === hour);
    return {
      label: `${hour.toString().padStart(2, '0')}:00`,
      pv: hourVisits.length,
      uv: unique(hourVisits.map((v) => v.visitorId)),
      ip: unique(hourVisits.map((v) => v.ip)),
    };
  });
}

export function buildSources(visits: Visit[]): NamedCount[] {
  const map = new Map<SourceChannel, number>(CHANNELS.map((c) => [c, 0]));
  visits.forEach((v) => {
    const channel = classifySource(v.referrer, v.pageUrl);
    map.set(channel, (map.get(channel) || 0) + 1);
  });
  return CHANNELS.map((name) => ({ name, value: map.get(name) || 0 }));
}

export function buildEngines(visits: Visit[]): NamedCount[] {
  return countBy(
    visits.map((v) => parseSearch(v.referrer, v.pageUrl).engine).filter(Boolean)
  );
}

export function buildKeywords(visits: Visit[]) {
  const groups = new Map<string, Visit[]>();
  visits.forEach((v) => {
    const { keyword, engine } = parseSearch(v.referrer, v.pageUrl);
    if (!keyword) return;
    const key = `${engine}::${keyword}`;
    const list = groups.get(key) || [];
    list.push(v);
    groups.set(key, list);
  });
  return [...groups.entries()]
    .map(([key, list]) => {
      const sep = key.indexOf('::');
      const engine = sep >= 0 ? key.slice(0, sep) : '';
      const keyword = sep >= 0 ? key.slice(sep + 2) : key;
      return {
        keyword,
        engine,
        pv: list.length,
        uv: unique(list.map((v) => v.visitorId)),
      };
    })
    .sort((a, b) => b.pv - a.pv);
}

export function buildReferrers(visits: Visit[]) {
  return countBy(
    visits
      .filter((v) => v.referrer && classifySource(v.referrer, v.pageUrl) === '外部链接')
      .map((v) => referrerHost(v.referrer))
  );
}

export function splitNewReturn(visits: Visit[], allVisits: Visit[], periodFrom: number) {
  const seen = firstSeenMap(allVisits);
  const news = visits.filter((v) => (seen.get(v.visitorId) || 0) >= periodFrom);
  const olds = visits.filter((v) => (seen.get(v.visitorId) || 0) < periodFrom);
  return { news, olds };
}

export function buildSocial(visits: Visit[]): NamedCount[] {
  return countBy(
    visits
      .filter((v) => classifySource(v.referrer, v.pageUrl) === '社交媒体')
      .map((v) => socialName(v.referrer))
  );
}

export function buildLanguages(visits: Visit[]): NamedCount[] {
  return countBy(visits.map((v) => v.language || '未知'));
}

export function buildResolutions(visits: Visit[]): NamedCount[] {
  return countBy(visits.map((v) => v.screen || '未知'));
}

export function buildVisitedHosts(visits: Visit[], fallbackDomain: string): NamedCount[] {
  return countBy(visits.map((v) => visitPageHost(v.pageUrl, fallbackDomain) || fallbackDomain || '本站'));
}

export function buildPages(visits: Visit[]) {
  const sessions = buildSessions(visits);
  const entryCount = new Map<string, number>();
  const exitCount = new Map<string, number>();
  sessions.forEach((s) => {
    const entry = visitPagePath(s.pages[0]?.pageUrl || '/');
    const exit = visitPagePath(s.pages[s.pages.length - 1]?.pageUrl || '/');
    entryCount.set(entry, (entryCount.get(entry) || 0) + 1);
    exitCount.set(exit, (exitCount.get(exit) || 0) + 1);
  });

  const groups = new Map<string, Visit[]>();
  visits.forEach((v) => {
    const key = visitPagePath(v.pageUrl || '/');
    const list = groups.get(key) || [];
    list.push(v);
    groups.set(key, list);
  });

  return [...groups.entries()]
    .map(([path, list]) => {
      const uv = unique(list.map((v) => v.visitorId));
      const withDuration = list.filter((v) => v.durationMs > 0);
      const avgDuration =
        withDuration.length === 0
          ? 0
          : withDuration.reduce((sum, v) => sum + v.durationMs, 0) / withDuration.length;
      return {
        path,
        pv: list.length,
        uv,
        avgDuration,
        entry: entryCount.get(path) || 0,
        exit: exitCount.get(path) || 0,
        bounce: sessions.filter((s) => s.pages.length === 1 && visitPagePath(s.pages[0]?.pageUrl || '/') === path).length,
        bounceRate: (entryCount.get(path) || 0)
          ? sessions.filter((s) => s.pages.length === 1 && visitPagePath(s.pages[0]?.pageUrl || '/') === path).length /
            (entryCount.get(path) || 1)
          : 0,
      };
    })
    .sort((a, b) => b.pv - a.pv);
}

export function buildGeo(visits: Visit[]): NamedCount[] {
  return countBy(
    visits.map((v) => (v.city && v.city !== '未知城市' ? v.city : v.country || '未知'))
  );
}

export function buildProvinces(visits: Visit[]): NamedCount[] {
  return countBy(visits.map((v) => cityToProvince(v.city, v.country, v.province)));
}

export function buildIsp(visits: Visit[]): NamedCount[] {
  return countBy(visits.map((v) => inferIsp(v.ip, v.isp)));
}

export function buildDevices(visits: Visit[]): NamedCount[] {
  return countBy(
    visits.map((v) => (v.device === 'mobile' ? '手机' : v.device === 'tablet' ? '平板' : '电脑'))
  );
}

export function buildBrowsers(visits: Visit[]): NamedCount[] {
  return countBy(visits.map((v) => v.browser));
}

export function buildOs(visits: Visit[]): NamedCount[] {
  return countBy(visits.map((v) => v.os));
}

export function buildSpiders(visits: Visit[]): NamedCount[] {
  return countBy(visits.map((v) => detectSpider(v.userAgent)).filter(Boolean));
}

export function buildVisitorType(visits: Visit[], allVisits: Visit[], periodFrom?: number) {
  const stats = summarizePeriod(visits, allVisits, periodFrom);
  return [
    { name: '新访客', value: stats.newUv },
    { name: '回访客', value: stats.returnUv },
  ];
}

export function realtimeVisits(visits: Visit[], minutes = 5): Visit[] {
  const from = Date.now() - minutes * 60_000;
  return visits.filter((v) => new Date(v.createdAt).getTime() >= from);
}

export function onlineCount(visits: Visit[]): number {
  return unique(realtimeVisits(visits, 5).map((v) => v.visitorId));
}
